import { useMemo, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, FileUp, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { CRM_FIELDS, parseCsv, titleCase } from "@/lib/crm";

const clean = (key, v) => {
  const s = (v || "").trim();
  if (key === "email") return s.toLowerCase();
  if (key === "first_name" || key === "last_name") return titleCase(s);
  return s;
};

const SKIP = "__skip__";
const autoMap = (headers) =>
  Object.fromEntries(CRM_FIELDS.map((f) => {
    const i = headers.findIndex((h) => f.match.includes(h.toLowerCase()));
    return [f.key, i >= 0 && f.match.slice(0, 2).includes(headers[i].toLowerCase()) ? String(i) : SKIP];
  }));

export default function ImportWizard({ open, onOpenChange, onImported }) {
  const [step, setStep] = useState(1);
  const [csv, setCsv] = useState(null);
  const [map, setMap] = useState({});
  const [tag, setTag] = useState("");
  const [check, setCheck] = useState(null);
  const [count, setCount] = useState(0);
  const [busy, setBusy] = useState(false);

  const reset = () => { setStep(1); setCsv(null); setMap({}); setTag(""); setCheck(null); };
  const close = (v) => { if (!v) reset(); onOpenChange(v); };

  const load = (text, name) => {
    const parsed = parseCsv(text);
    if (!parsed.headers.length || !parsed.rows.length) return toast.error("That file has no rows to import");
    setCsv({ ...parsed, name });
    setMap(autoMap(parsed.headers));
    setStep(2);
  };

  const onFile = async (file) => {
    if (!file) return;
    load(await file.text(), file.name);
    const fd = new FormData();
    fd.append("file", file);
    api.post("/imports/files", fd).catch(() => toast.warning("CSV loaded, but the original file could not be archived"));
  };

  const records = useMemo(() => {
    if (!csv) return [];
    return csv.rows.map((r) => Object.fromEntries(CRM_FIELDS.map((f) => [f.key, map[f.key] !== SKIP ? clean(f.key, r[Number(map[f.key])]) : ""])));
  }, [csv, map]);

  const validate = () => {
    const errors = [];
    const used = Object.values(map).filter((v) => v !== SKIP);
    if (map.first_name === SKIP) errors.push("First Name must be mapped to a column.");
    if (new Set(used).size !== used.length) errors.push("Each column can only be mapped to one CRM field.");
    const noName = records.filter((r) => !r.first_name).length;
    if (map.first_name !== SKIP && noName) errors.push(`${noName} row(s) have no First Name.`);
    const badEmail = records.filter((r) => r.email && !/^\S+@\S+\.\S+$/.test(r.email)).length;
    if (badEmail) errors.push(`${badEmail} row(s) have an invalid email.`);
    setCheck({ ok: errors.length === 0, errors });
  };

  const doImport = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/contacts/import", { records: records.map((r) => ({ ...r, status: "New" })), tags: tag ? [tag.trim()] : [] });
      setCount(data.imported);
      setStep(3);
      onImported();
      toast.success(`${data.imported} records imported`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-2xl" data-testid="import-wizard">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Import Records</DialogTitle>
          <div className="flex gap-2 pt-2 text-xs">
            {["Upload", "Map & Validate", "Done"].map((s, i) => (
              <span key={s} className={`rounded-full px-3 py-1 font-semibold ${step === i + 1 ? "bg-[#3B185F] text-white" : step > i + 1 ? "bg-[#D4AF37] text-[#19082B]" : "bg-stone-100 text-stone-500"}`}>{i + 1}. {s}</span>
            ))}
          </div>
        </DialogHeader>

        {step === 1 && (
          <label className="block cursor-pointer rounded-xl border-2 border-dashed border-[#3B185F]/25 p-10 text-center hover:border-[#D4AF37] hover:bg-[#FFFDF0] transition-colors" data-testid="import-file-dropzone">
            <FileUp className="mx-auto h-8 w-8 text-[#3B185F]" />
            <div className="mt-3 font-semibold">Upload a CSV file</div>
            <div className="text-xs text-stone-500">Click to browse. The first row should contain column headers.</div>
            <input type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => onFile(e.target.files[0])} data-testid="import-file-input" />
          </label>
        )}

        {step === 2 && csv && (
          <div className="space-y-5">
            <p className="text-sm text-stone-600"><span className="font-semibold">{csv.name}</span> · {csv.rows.length} rows found. Match each field to a column in your file. Names and emails are tidied automatically.</p>
            <div className="grid grid-cols-2 gap-3">
              {CRM_FIELDS.map((f) => (
                <div key={f.key} className="space-y-1">
                  <Label className="text-xs">{f.label}{f.required && " *"}</Label>
                  <Select value={map[f.key]} onValueChange={(v) => { setMap((m) => ({ ...m, [f.key]: v })); setCheck(null); }}>
                    <SelectTrigger data-testid={`map-${f.key}-select`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={SKIP}>— Don't import —</SelectItem>
                      {csv.headers.map((h, i) => <SelectItem key={i} value={String(i)} data-testid={`map-${f.key}-option-${i}`}>{h}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
              <div className="space-y-1">
                <Label className="text-xs">Tag all imported records (optional)</Label>
                <Input value={tag} onChange={(e) => setTag(e.target.value)} placeholder="e.g. AVY Clients" data-testid="import-tag-input" />
              </div>
            </div>
            <div className="overflow-x-auto rounded-lg border text-xs">
              <table className="w-full">
                <thead className="bg-[#F6F2FB]"><tr>{CRM_FIELDS.map((f) => <th key={f.key} className="p-2 text-left font-semibold">{f.label}</th>)}</tr></thead>
                <tbody>{records.slice(0, 3).map((r, i) => <tr key={i} className="border-t">{CRM_FIELDS.map((f) => <td key={f.key} className="p-2 truncate max-w-[140px]">{r[f.key] || <span className="text-stone-300">—</span>}</td>)}</tr>)}</tbody>
              </table>
            </div>
            {check && (
              <div className={`rounded-lg border p-3 text-sm ${check.ok ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-red-50 border-red-200 text-red-700"}`} data-testid="mapping-validation-result">
                {check.ok ? <span className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4" /> Mapping validated — {records.length} records ready.</span>
                  : check.errors.map((e) => <div key={e} className="flex items-center gap-2"><AlertTriangle className="h-4 w-4" />{e}</div>)}
              </div>
            )}
            <div className="flex justify-between">
              <Button variant="outline" onClick={reset}>Back</Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={validate} className="border-[#D4AF37]" data-testid="validate-mapping-btn">Validate Mapping</Button>
                <Button disabled={!check?.ok || busy} onClick={doImport} className="bg-[#3B185F] hover:bg-[#250E3F]" data-testid="complete-import-btn">{busy ? "Importing…" : "Complete Import"}</Button>
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="text-center py-6 space-y-4" data-testid="import-success">
            <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-600" />
            <div className="font-display text-2xl font-bold text-[#19082B]">Import complete</div>
            <p className="text-sm text-stone-600"><span className="font-semibold" data-testid="import-success-count">{count}</span> records were added to Contacts with status New.</p>
            <Button onClick={() => close(false)} className="bg-[#3B185F]" data-testid="import-view-contacts-btn">View contacts</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
